/**
 * SB6-II-01 — facet + option query hooks for the inspection-instructions
 * page.
 *
 * - Projects facet is **eager** (loaded at mount).
 * - Sites / Asset facets are **async typeahead** — the parent owns the
 *   input-query state and the query is keyed on it; nothing fetches
 *   until the user types (perf — §Filters 3/4).
 * - Cascade option hooks (`options/projects`, `options/contracts`,
 *   `options/contract-positions`) drive the Add modal + Edit re-link.
 * - `assignable-users` and `options/companies` back the inline cells.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { projectsFetch } from "../projects/projectsFetch.js";
import { iiKeys } from "./keys.js";
import type {
  IiAssignableUsersResponse,
  IiCascadeOptionsResponse,
  IiCompanyOptionsResponse,
  IiFacetResponse,
} from "./types.js";

const BASE = "/inspection-instructions";

/** Eager Projects facet — loaded at mount. */
export function useIiProjectFacetQuery() {
  return useQuery<IiFacetResponse>({
    queryKey: iiKeys.projectFacet(),
    queryFn: () => projectsFetch<IiFacetResponse>(`${BASE}/facets/projects`),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Async Sites facet — keyed on the typed query; idle until the user types. */
export function useIiSiteFacetQuery(q: string) {
  return useQuery<IiFacetResponse>({
    queryKey: iiKeys.siteFacet(q),
    enabled: q.trim().length > 0,
    queryFn: () =>
      projectsFetch<IiFacetResponse>(
        `${BASE}/facets/sites?q=${encodeURIComponent(q)}`,
      ),
    staleTime: 60_000,
    retry: false,
  });
}

/** Async Asset facet — keyed on the typed query; idle until the user types. */
export function useIiAssetFacetQuery(q: string) {
  return useQuery<IiFacetResponse>({
    queryKey: iiKeys.assetFacet(q),
    enabled: q.trim().length > 0,
    queryFn: () =>
      projectsFetch<IiFacetResponse>(
        `${BASE}/facets/assets?q=${encodeURIComponent(q)}`,
      ),
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Convenience container so the page can own the two async-filter query
 * strings without re-declaring the boilerplate per filter.
 */
export function useInspectionInstructionFacets() {
  const [siteQuery, setSiteQuery] = useState("");
  const [assetQuery, setAssetQuery] = useState("");
  const projects = useIiProjectFacetQuery();
  const sites = useIiSiteFacetQuery(siteQuery);
  const assets = useIiAssetFacetQuery(assetQuery);
  return {
    projects,
    sites,
    assets,
    siteQuery,
    setSiteQuery,
    assetQuery,
    setAssetQuery,
  };
}

/** Assignable-users for the inline Assigned Users cell + bulk-assign.
 *  Lazy-loaded — the caller passes `enabled` (popover/dialog open). */
export function useIiAssignableUsersQuery(enabled: boolean) {
  return useQuery<IiAssignableUsersResponse>({
    queryKey: iiKeys.assignableUsers(null),
    enabled,
    queryFn: () =>
      projectsFetch<IiAssignableUsersResponse>(`${BASE}/assignable-users`),
    staleTime: 30_000,
    retry: false,
  });
}

/** Active-state projects for the Add / re-link cascade. */
export function useIiProjectOptionsQuery(enabled: boolean) {
  return useQuery<IiCascadeOptionsResponse>({
    queryKey: iiKeys.projectOptions(),
    enabled,
    queryFn: () =>
      projectsFetch<IiCascadeOptionsResponse>(`${BASE}/options/projects`),
    staleTime: 60_000,
    retry: false,
  });
}

/** Contracts within a picked project. */
export function useIiContractOptionsQuery(projectId: string | null) {
  return useQuery<IiCascadeOptionsResponse>({
    queryKey: iiKeys.contractOptions(projectId),
    enabled: typeof projectId === "string" && projectId.length > 0,
    queryFn: () =>
      projectsFetch<IiCascadeOptionsResponse>(
        `${BASE}/options/contracts?projectId=${encodeURIComponent(projectId ?? "")}`,
      ),
    staleTime: 60_000,
    retry: false,
  });
}

/** Positions within a picked contract's site. */
export function useIiContractPositionOptionsQuery(contractId: string | null) {
  return useQuery<IiCascadeOptionsResponse>({
    queryKey: iiKeys.contractPositionOptions(contractId),
    enabled: typeof contractId === "string" && contractId.length > 0,
    queryFn: () =>
      projectsFetch<IiCascadeOptionsResponse>(
        `${BASE}/options/contract-positions?contractId=${encodeURIComponent(contractId ?? "")}`,
      ),
    staleTime: 60_000,
    retry: false,
  });
}

/** Assignable companies for the inline Assigned Company cell.
 *  Interim: BE returns own-company-only (AC24). */
export function useIiCompanyOptionsQuery(q: string, enabled: boolean) {
  return useQuery<IiCompanyOptionsResponse>({
    queryKey: iiKeys.companyOptions(q),
    enabled,
    queryFn: () =>
      projectsFetch<IiCompanyOptionsResponse>(
        `${BASE}/options/companies?q=${encodeURIComponent(q)}`,
      ),
    staleTime: 60_000,
    retry: false,
  });
}
